import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Info, Gauge, CalendarClock, PlayCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import hostVslIcon from '@/assets/hostvsl-icon.webp';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { normalizePlanName } from '@/utils/planName';

// Plays do período de teste (espelha TRIAL_LIMIT em e-video-vsl-be/helpers/plan-limits.js)
const TRIAL_PLAYS = 300;

interface PlanUsageSectionProps {
  onAddPlaysClick: () => void;
}

export const PlanUsageSection: React.FC<PlanUsageSectionProps> = ({ onAddPlaysClick }) => {
  useLanguage();
  const navigate = useNavigate();

  // Busca dados do usuário e plano do contexto de autenticação
  const { user, plan, isLoading } = useCustomAuth();

  // Dados do plano vindo de /auth/me (dados reais do banco)
  const isTrial = plan?.status === 'trialing' || plan?.periodo_teste === true;
  const isCanceled = !plan || plan.status === 'canceled';
  const cleanPlanNome = normalizePlanName(plan?.nome);
  const planName = isTrial && cleanPlanNome && cleanPlanNome !== 'Teste Gratuito'
    ? `${cleanPlanNome} (Teste)`
    : cleanPlanNome || 'Teste Gratuito';
  const totalPlays = isTrial ? TRIAL_PLAYS : (plan?.clicks_total || 300);
  // No trial o backend consome `clicks_per_test` (saldo de 300 que decrementa),
  // não `clicks_per_plan` — então `plan.clicks_usados`, derivado do plano, fica
  // 0. Membros não recebem `clicks_per_test` e caem no valor do plano.
  const trialPlaysLeft = user?.clicks_per_test;
  const playsUsed = isTrial && trialPlaysLeft != null
    ? Math.max(TRIAL_PLAYS - trialPlaysLeft, 0)
    : plan?.clicks_usados || 0;
  const percentageUsed = totalPlays > 0 ? Math.round((playsUsed / totalPlays) * 100) : 0;
  const daysUntilRenewal: number = plan?.dias_renovacao || user?.dias_renovacao || 0;
  const playsExtras = plan?.excess_clicks || 0;

  // Estado de carregamento: esqueleto com a mesma silhueta do card real
  if (isLoading) {
    return (
      <motion.div
        className="w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <div
          className="rounded-[16px] border border-[#DCE3ED] bg-[#F8FAFC] p-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)] animate-pulse"
          style={{ height: '210px' }}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <div className="rounded-[16px] border border-[#DCE3ED] bg-[#F8FAFC] p-4 shadow-[0_4px_12px_rgba(0,0,0,0.03)]">
        {/* Header: nome do plano + upgrade */}
        <div className={isCanceled ? "flex items-center justify-between mb-4" : "flex items-center justify-between mb-5"}>
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-lg bg-[#F0F5FF] flex items-center justify-center">
              <img src={hostVslIcon} alt="" className="w-3.5 h-3.5 object-contain" />
            </div>
            <span className="text-[14px] font-semibold text-[#101828] dark:text-foreground">
              {isCanceled ? translateInterfaceText("Sem plano ativo") : translateInterfaceText("Plano {value0}", { value0: (planName) })}
            </span>
          </div>
          {!isCanceled && (
            <button
              onClick={() => navigate('/checkout')}
              className="text-[11px] font-bold text-[#3478F6] hover:text-[#2863D1] transition-all active:scale-95"
            >
              {translateInterfaceText("Upgrade")}</button>
          )}
        </div>

        {isCanceled ? (
          // Sem plano ativo: apenas o CTA de ativação
          <button
            onClick={() => navigate('/checkout')}
            className="w-full h-[36px] rounded-[9px] bg-[#3478F6] text-white text-[12px] font-bold transition-all duration-200 hover:bg-[#2863D1] active:scale-[0.98] shadow-[0_4px_10px_rgba(52,120,246,0.2)]"
          >
            {translateInterfaceText("Ativar o plano")}</button>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  {/* O ícone preenche junto com a barra: uma cópia apagada como
                      base e outra azul por cima, recortada na mesma proporção do
                      uso. O ícone de dentro mantém largura fixa (shrink-0) para
                      ser cortado, e não espremido, quando o wrapper encolhe. */}
                  <span className="relative inline-flex w-[15px] h-[15px] flex-shrink-0">
                    <Gauge className="absolute inset-0 w-[15px] h-[15px] text-[#CBD5E1]" />
                    <motion.span
                      className="absolute inset-0 overflow-hidden"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(percentageUsed, 100)}%` }}
                      transition={{ duration: 1.5, ease: 'easeOut' }}
                    >
                      <Gauge className="w-[15px] h-[15px] text-[#3478F6] flex-shrink-0 max-w-none" />
                    </motion.span>
                  </span>
                  <span className="text-[11px] font-medium text-[#667085]">{translateInterfaceText("Uso do Plano")}</span>
                </div>
                <span className="text-[11px] font-semibold text-[#101828] dark:text-foreground">
                  {playsUsed.toLocaleString(getInterfaceLanguage())} <span className="text-[#98A2B3] font-medium">/ {totalPlays.toLocaleString(getInterfaceLanguage())}</span>
                </span>
              </div>

              <div className="w-full h-3.5 bg-[#F2F4F7] rounded-full overflow-hidden relative border border-[#DCE3ED]">
                <motion.div
                  className="h-full bg-[#3478F6] rounded-full relative flex items-center justify-center min-w-[32px]"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(percentageUsed, 100)}%` }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                >
                  <span className="text-[9px] font-bold text-white relative z-10 leading-none">
                    {percentageUsed}%
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent w-[50%]"
                    animate={{
                      left: ['-50%', '100%'],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </motion.div>
              </div>
            </div>

            <div className="h-px bg-[#EEF1F5]" />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-[15px] h-[15px] text-[#98A2B3]" />
                  <span className="text-[11px] font-medium text-[#667085]">{translateInterfaceText("Reinicia em")}</span>
                </div>
                <span className="text-[11px] font-semibold text-[#101828]">
                  {daysUntilRenewal} {daysUntilRenewal === 1 ? translateInterfaceText("dia") : translateInterfaceText("dias")}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PlayCircle className="w-[15px] h-[15px] text-[#98A2B3]" />
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-medium text-[#667085]">{translateInterfaceText("Plays extras")}</span>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button className="p-0.5 rounded-full hover:bg-gray-100 transition-colors">
                            <Info className="w-3 h-3 text-[#98A2B3]" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[200px] whitespace-normal text-[10px] leading-[1.35] bg-white text-[#475467] border-[#DCE3ED] shadow-lg rounded-[8px] px-2.5 py-2">
                          {translateInterfaceText("Use plays extras se o limite mensal expirar antes da renovação.")}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-[#101828]">
                  {playsExtras.toLocaleString(getInterfaceLanguage())}
                </span>
              </div>
            </div>

            <button
              onClick={onAddPlaysClick}
              className="w-full h-[36px] rounded-[9px] bg-[#3478F6] text-white text-[12px] font-bold transition-all duration-200 hover:bg-[#2863D1] active:scale-[0.98] shadow-[0_4px_10px_rgba(52,120,246,0.2)]"
            >
              {translateInterfaceText("+ Adicionar Plays Extras")}</button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
